import { useEffect, useState, useCallback } from "react";
import { Plus, Trash, TrendUp, TrendDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { formatMoney, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const INCOME_CATS = ["Satış", "Hizmet", "Faiz", "Tahsilat", "Diğer Gelir"];
const EXPENSE_CATS = ["Maaş", "Kira", "Tedarik", "Elektrik/Su", "Pazarlama", "Vergi", "Lojistik", "Ödeme", "Diğer"];

const empty = { type: "income", amount: "", currency: "TRY", category: "Satış", account_id: "", description: "", date: "" };

export default function Transactions() {
  const { currencies } = useCurrency();
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    api.get("/transactions").then((r) => setItems(r.data)).catch(() => {});
    api.get("/accounts").then((r) => setAccounts(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setForm(empty); setOpen(true); };

  const submit = async () => {
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Geçerli bir tutar girin");
    try {
      await api.post("/transactions", {
        ...form,
        amount: Number(form.amount),
        account_id: form.account_id || null,
        date: form.date ? new Date(form.date).toISOString() : null,
      });
      toast.success("İşlem eklendi");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("İşlem eklenemedi");
    }
  };

  const remove = async (id) => {
    await api.delete(`/transactions/${id}`);
    toast.success("İşlem silindi");
    load();
  };

  const filtered = items.filter((i) => filter === "all" || i.type === filter);
  const accName = (id) => accounts.find((a) => a.id === id)?.name || "-";

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-light tracking-tight">Gelir & Gider</h1>
          <p className="text-sm text-muted-foreground mt-1">Tüm gelir ve gider hareketlerinizi yönetin</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="add-transaction-btn" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus size={18} className="mr-1" /> Yeni İşlem
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader><DialogTitle className="font-heading">Yeni İşlem</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Tür</Label>
                  <Select value={form.type} onValueChange={(v) => set("type", v)}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="tx-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="income">Gelir</SelectItem>
                      <SelectItem value="expense">Gider</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Kategori</Label>
                  <Select value={form.category} onValueChange={(v) => set("category", v)}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="tx-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(form.type === "income" ? INCOME_CATS : EXPENSE_CATS).map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Tutar</Label>
                  <Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)}
                    className="mt-1.5 bg-secondary border-border" data-testid="tx-amount" placeholder="0" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Para Birimi</Label>
                  <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="tx-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Hesap</Label>
                  <Select value={form.account_id} onValueChange={(v) => set("account_id", v)}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="tx-account"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                    <SelectContent>
                      {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Tarih</Label>
                  <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)}
                    className="mt-1.5 bg-secondary border-border" data-testid="tx-date" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Açıklama</Label>
                <Textarea value={form.description} onChange={(e) => set("description", e.target.value)}
                  className="mt-1.5 bg-secondary border-border" data-testid="tx-description" rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} data-testid="tx-save" className="bg-primary text-primary-foreground hover:bg-primary/90">Kaydet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-2">
        {[["all", "Tümü"], ["income", "Gelir"], ["expense", "Gider"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`filter-${v}`}
            className={`px-4 py-1.5 rounded-md text-sm transition-colors ${filter === v ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Tür</TableHead>
              <TableHead>Kategori</TableHead>
              <TableHead>Hesap</TableHead>
              <TableHead>Tarih</TableHead>
              <TableHead className="text-right">Tutar</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">Kayıt bulunamadı.</TableCell></TableRow>
            ) : filtered.map((t) => (
              <TableRow key={t.id} className="border-border" data-testid={`tx-row-${t.id}`}>
                <TableCell>
                  <Badge variant="outline" className={t.type === "income" ? "border-[#10B981]/40 text-[#10B981]" : "border-[#EF4444]/40 text-[#EF4444]"}>
                    {t.type === "income" ? <TrendUp size={12} className="mr-1" /> : <TrendDown size={12} className="mr-1" />}
                    {t.type === "income" ? "Gelir" : "Gider"}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">
                  {t.category}
                  {t.created_by_name && (
                    <div className="mt-1">
                      <Badge variant="outline" data-testid={`tx-enteredby-${t.id}`}
                        className={t.created_by_role === "staff" ? "border-primary/40 text-primary text-[10px]" : "border-border text-muted-foreground text-[10px]"}>
                        {t.created_by_role === "staff" ? "👤 " : ""}{t.created_by_name}
                      </Badge>
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{accName(t.account_id)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{formatDate(t.date)}</TableCell>
                <TableCell className={`text-right font-mono-num font-medium ${t.type === "income" ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                  {t.type === "income" ? "+" : "-"}{formatMoney(t.amount, t.currency, currencies)}
                </TableCell>
                <TableCell>
                  <ConfirmDelete onConfirm={() => remove(t.id)} testId={`tx-delete-${t.id}`}
                    className="text-muted-foreground hover:text-destructive transition-colors"
                    title="Kaydı sil?" description="Bu gelir/gider kaydı silinecek ve bağlı hesap bakiyesi geri düzeltilecek.">
                    <Trash size={16} />
                  </ConfirmDelete>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
